import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { GraduationCap, Cross } from "lucide-react";

const AUTH_BG =
  "https://customer-assets-v7afamib.emergentagent.net/job_learn-admin-45/artifacts/awbo1tcr_WhatsApp%20Image%202026-08-10%20at%2010.01.34%20AM.jpeg";

// Presentation welcome banner — auto-hides after Sun 27 Sep 2026, 23:59 Haiti time (UTC-4).
const PRESENTATION_DEADLINE = Date.parse("2026-09-27T23:59:00-04:00");

const TEST_EMAIL_DOMAINS = ["icdh.demo", "icdh.edu", "demo", "test", "example.com", "example.org", "localhost", "invalid", "none", "na"];
// True only for a syntactically valid, non-test email (mirrors the backend rules).
function emailFormatOk(email) {
  const e = (email || "").trim().toLowerCase();
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) return false;
  const dom = e.split("@").pop();
  if (TEST_EMAIL_DOMAINS.includes(dom)) return false;
  return !/\.(demo|test|invalid|local|example)$/.test(dom);
}

export default function Login() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", student_code: "", pin: "" });
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const { setUser } = useAuth();
  const { L } = useLang();
  const navigate = useNavigate();
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const showBanner = Date.now() < PRESENTATION_DEADLINE;

  const submit = async (e) => {
    e.preventDefault();
    if ((mode === "login" || mode === "register") && !agreed) {
      toast.error(L("Please agree to the Terms and Conditions to continue.", "Veuillez accepter les termes et conditions pour continuer."));
      return;
    }
    if (mode === "register" && !emailFormatOk(form.email)) {
      toast.error(L("Please enter a valid email address (no test domain) — it will be used for all school communications.", "Veuillez entrer une adresse email valide (sans domaine de test) — elle servira à toutes les communications de l'école."));
      return;
    }
    setLoading(true);
    try {
      let u;
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: form.email });
        toast.success(L("If that email exists, a reset link has been sent.", "Si cet e-mail existe, un lien de réinitialisation a été envoyé."));
        setMode("login");
        return;
      } else if (mode === "student") {
        u = await api.post("/auth/student-login", { student_code: form.student_code, pin: form.pin });
      } else if (mode === "login") {
        u = await api.post("/auth/login", { email: form.email, password: form.password });
      } else {
        u = await api.post("/auth/register", { name: form.name, email: form.email, password: form.password, phone: form.phone });
      }
      setUser(u);
      toast.success(mode === "register" ? "Account created!" : "Welcome!");
      navigate("/app");
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-primary text-primary-foreground overflow-hidden">
        <div className="absolute inset-0 opacity-45" style={{ backgroundImage: `url(${AUTH_BG})`, backgroundSize: "cover", backgroundPosition: "center", filter: "blur(3px)", transform: "scale(1.05)" }} />
        <div className="absolute inset-0 bg-primary/70" />
        <div className="relative z-10 flex items-center gap-3">
          <div className="h-20 w-20 rounded-xl bg-white flex items-center justify-center overflow-hidden shadow p-1.5">
            <img src={`${API}/settings/logo`} alt="ICDH" className="h-full w-full object-contain"
              onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }} />
            <div className="h-full w-full items-center justify-center bg-accent rounded-lg" style={{ display: "none" }}><Cross className="h-7 w-7 text-primary" /></div>
          </div>
          <div>
            <p className="font-heading font-extrabold text-lg leading-tight">Institution Chrétienne</p>
            <p className="text-accent text-sm font-heading">D'Haïti — ICDH</p>
            <p className="font-heading font-semibold mt-0.5" style={{ fontSize: "14px", color: "#ef4444" }}>L'école de l'Avenir</p>
          </div>
        </div>
        <div className="relative z-10 max-w-md">
          <p className="text-accent font-heading font-bold text-sm uppercase tracking-widest mb-3">{L("School Management System", "Système de Gestion Scolaire")}</p>
          <h1 className="font-heading font-extrabold text-4xl xl:text-5xl tracking-tight leading-tight">
            {L("One portal for your whole school community.", "Un portail unique pour toute la communauté scolaire.")}
          </h1>
          <p className="mt-5 text-primary-foreground/80 text-base leading-relaxed">
            {L("Manage enrollment, report cards, fees, homework and communications — for administrators, teachers and parents.",
              "Gérez inscriptions, bulletins, frais, devoirs et communications — pour l'administration, les enseignants et les parents.")}
          </p>
        </div>
        <div className="relative z-10 text-primary-foreground/60 text-sm">Port-au-Prince, Haïti</div>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center p-6 sm:p-10 icdh-grain">
        <div className="w-full max-w-md animate-fade-up">
          {showBanner && (
            <div data-testid="presentation-banner" className="mb-6 rounded-xl border border-accent/50 bg-accent/15 px-4 py-3 shadow-sm">
              <p className="font-heading font-bold text-primary text-sm leading-snug">
                {L("Welcome to the official presentation of the ICDH digital ecosystem — Sunday, September 27",
                  "Bienvenue à la présentation officielle de l'écosystème numérique ICDH — Dimanche 27 Septembre")}
              </p>
            </div>
          )}
          <div className="flex items-center gap-2 lg:hidden mb-8">
            <div className="h-10 w-10 rounded-lg bg-white flex items-center justify-center overflow-hidden shadow">
              <img src={`${API}/settings/logo`} alt="ICDH" className="h-full w-full object-contain"
                onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }} />
              <div className="h-full w-full items-center justify-center bg-primary" style={{ display: "none" }}><GraduationCap className="h-5 w-5 text-primary-foreground" /></div>
            </div>
            <span className="font-heading font-extrabold text-primary">ICDH Portal</span>
          </div>

          <h2 className="font-heading font-extrabold text-3xl tracking-tight text-foreground">
            {mode === "login" ? L("Sign in", "Connexion") : mode === "student" ? L("Student Exam Login", "Connexion Élève") : mode === "forgot" ? L("Reset your password", "Réinitialiser le mot de passe") : L("Create parent account", "Créer un compte parent")}
          </h2>
          <p className="text-muted-foreground mt-2 mb-8">
            {mode === "login"
              ? L("Access your dashboard.", "Accédez à votre espace.")
              : mode === "student"
              ? L("Enter the Student ID and PIN given by the school.", "Entrez l'identifiant et le code PIN fournis par l'école.")
              : mode === "forgot"
              ? L("Enter your email and we'll send a reset link.", "Entrez votre e-mail pour recevoir un lien de réinitialisation.")
              : L("Register to enroll your children at ICDH.", "Inscrivez-vous pour enregistrer vos enfants à l'ICDH.")}
          </p>

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {mode === "student" ? (
              <>
                <div className="space-y-2">
                  <Label>{L("Student ID", "Identifiant élève")}</Label>
                  <Input required value={form.student_code} onChange={upd("student_code")} placeholder="AB CD1234" data-testid="student-code-input" />
                </div>
                <div className="space-y-2">
                  <Label>PIN</Label>
                  <Input required type="password" value={form.pin} onChange={upd("pin")} placeholder="••••" data-testid="student-pin-input" />
                </div>
              </>
            ) : (
              <>
                {mode === "register" && (
                  <div className="space-y-2">
                    <Label>{L("Full name", "Nom complet")}</Label>
                    <Input required value={form.name} onChange={upd("name")} placeholder="Marie Joseph" data-testid="register-name-input" />
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input required type="email" value={form.email} onChange={upd("email")} placeholder="you@example.com" data-testid="email-input" />
                  {mode === "register" && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 leading-snug" data-testid="email-communications-note">
                      {L("⚠️ Please double-check your email address — it will be used for all school communications (report cards, invoices, official letters).",
                        "⚠️ Vérifiez bien votre adresse email — elle servira à toutes les communications de l'école (bulletins, factures, lettres officielles).")}
                    </p>
                  )}
                </div>
                {mode === "register" && (
                  <div className="space-y-2">
                    <Label>{L("Phone (optional)", "Téléphone (optionnel)")}</Label>
                    <Input value={form.phone} onChange={upd("phone")} placeholder="+509 ..." data-testid="register-phone-input" />
                  </div>
                )}
                {mode !== "forgot" && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>{L("Password", "Mot de passe")}</Label>
                      {mode === "login" && (
                        <button type="button" className="text-xs text-primary hover:underline" onClick={() => setMode("forgot")} data-testid="forgot-password-link">
                          {L("Forgot password?", "Mot de passe oublié ?")}
                        </button>
                      )}
                    </div>
                    <Input required type="password" value={form.password} onChange={upd("password")} placeholder="••••••••" data-testid="password-input" />
                  </div>
                )}
              </>
            )}
            {(mode === "login" || mode === "register") && (
              <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer" data-testid="terms-agree-label">
                <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5" data-testid="terms-agree-checkbox" />
                <span>{L("I have read and agree to the school's Terms and Conditions and rules.", "J'ai lu et j'accepte les termes et conditions et le règlement de l'école.")}</span>
              </label>
            )}
            <Button type="submit" disabled={loading || ((mode === "login" || mode === "register") && !agreed)} className="w-full h-11 font-heading font-semibold" data-testid="auth-submit-button">
              {loading ? L("Please wait…", "Veuillez patienter…") : mode === "login" ? L("Sign in", "Se connecter") : mode === "student" ? L("Enter", "Entrer") : mode === "forgot" ? L("Send reset link", "Envoyer le lien") : L("Create account", "Créer le compte")}
            </Button>
          </form>

          {(mode === "login" || mode === "register") && (
            <>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
                <div className="relative flex justify-center"><span className="bg-background px-3 text-xs text-muted-foreground uppercase tracking-wide">{L("or", "ou")}</span></div>
              </div>
              <Button variant="outline" onClick={googleLogin} className="w-full h-11" data-testid="google-login-button">
                <img src="https://www.google.com/favicon.ico" alt="" className="h-4 w-4 mr-2" />
                {L("Continue with Google", "Continuer avec Google")}
              </Button>
            </>
          )}

          <div className="mt-6 space-y-2 text-center text-sm">
            {mode === "login" && (
              <p className="text-muted-foreground text-xs" data-testid="no-selfsignup-note">
                {L("New family? Please contact the school office to have your account created.", "Nouvelle famille ? Veuillez contacter le bureau de l'école pour créer votre compte.")}
              </p>
            )}
            <div className="flex items-center justify-center gap-4 pt-2 border-t border-border">
              <button type="button" className="text-primary font-medium hover:underline pt-2" onClick={() => setMode(mode === "student" ? "login" : "student")} data-testid="student-login-link">
                {mode === "student" ? L("Staff / Parent login", "Connexion personnel / parent") : L("Student exam login", "Connexion examen élève")}
              </button>
              <button type="button" className="text-accent-foreground font-medium hover:underline pt-2" onClick={() => navigate("/admission")} data-testid="admission-link">
                {L("Admission exam", "Examen d'admission")}
              </button>
              <button type="button" className="text-accent-foreground font-medium hover:underline pt-2" onClick={() => navigate("/entrance")} data-testid="entrance-link">
                {L("Entrance exam (PIN)", "Examen d'entrée (PIN)")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
